'use client';

import { useAppContext } from '@ifixit/app/hooks';
import {
   type RecentNotification,
   useMarkAllNotificationsRead,
   useMarkNotificationRead,
   useRecentNotifications,
} from '@ifixit/auth-sdk/client';
import { useFormatter, useNow, useTranslations } from '@ifixit/i18n/hooks';
import { useState } from 'react';
import { cn } from '../lib/utils';
import { BlueDot } from './blue-dot';
import { FaIcon } from './icon';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Skeleton } from './skeleton';

interface NotificationsMenuProps {
   unreadCount: number;
   /** The user's page listing every notification. */
   viewAllHref: string;
   className?: string;
}

/**
 * The header's notifications bell, shared by the Next.js and PHP headers. Its
 * list of the newest unread notifications loads when it opens.
 */
export function NotificationsMenu({ unreadCount, viewAllHref, className }: NotificationsMenuProps) {
   const t = useTranslations('components.NotificationsMenu');
   const [isOpen, setIsOpen] = useState(false);
   const recent = useRecentNotifications({ enabled: isOpen });
   const markRead = useMarkNotificationRead();
   const markAllRead = useMarkAllNotificationsRead();
   const notifications = recent.data?.notifications;

   return (
      // The wrapper takes `className`: the PHP header's unlayered
      // `.header-right-actions button { display: flex }` beats `hidden` on the button.
      <div className={className}>
         <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger
               aria-label={unreadCount > 0 ? t('unreadLabel', { count: unreadCount }) : t('label')}
               className="relative inline-flex size-10 cursor-pointer items-center justify-center rounded-full outline-none transition-opacity duration-300 hover:opacity-70 focus-visible:ring-[3px] focus-visible:ring-brand-400/70"
            >
               <FaIcon name="bell" className="size-6 text-white" />
               {unreadCount > 0 && <BlueDot className="absolute top-0.5 right-0.5 border-black" />}
            </PopoverTrigger>
            <PopoverContent className="popover-align-end w-75 rounded-lg p-1 text-sm outline-none">
               {notifications == null ? (
                  recent.isError ? (
                     <div className="px-2 py-1.5 text-gray-500">{t('error')}</div>
                  ) : (
                     <LoadingRows />
                  )
               ) : notifications.length === 0 ? (
                  <div className="px-2 py-1.5">{t('empty')}</div>
               ) : (
                  <>
                     <ul className="m-0 list-none p-0">
                        {notifications.map(notification => (
                           <NotificationRow
                              key={notification.signature}
                              notification={notification}
                              onMarkRead={() => markRead.mutate(notification.signature)}
                           />
                        ))}
                     </ul>
                     <div className="-mx-1 my-1 h-px bg-border" />
                     <div className="flex">
                        <a
                           href={viewAllHref}
                           className={cn(footerItemClassName, 'text-inherit no-underline')}
                        >
                           {t('viewAll')}
                        </a>
                        <button
                           type="button"
                           className={footerItemClassName}
                           onClick={() => markAllRead.mutate()}
                        >
                           {t('clear')}
                        </button>
                     </div>
                  </>
               )}
            </PopoverContent>
         </Popover>
      </div>
   );
}

type TileIcon = { style: 'solid' | 'regular'; name: string };

// Keyed by the icon each notification class sends, which stands for its kind.
// A few kinds draw a different glyph from the one sent.
const iconTiles: Record<string, { className: string; icon?: TileIcon }> = {
   check: { className: 'bg-green-100 text-green-700' },
   'circle-question': { className: 'bg-brand-100 text-brand-700' },
   'circle-exclamation': {
      className: 'bg-brand-100 text-brand-700',
      icon: { style: 'solid', name: 'circle-info' },
   },
   'pen-to-square': { className: 'bg-violet-100 text-violet-700' },
   certificate: {
      className: 'bg-green-100 text-green-700',
      icon: { style: 'solid', name: 'badge-check' },
   },
   xmark: {
      className: 'bg-red-100 text-red-700',
      icon: { style: 'solid', name: 'circle-exclamation' },
   },
   'cart-shopping': { className: 'bg-green-100 text-green-700' },
   gift: { className: 'bg-green-100 text-green-700' },
};

const footerItemClassName =
   'flex grow cursor-pointer items-center justify-center rounded-sm px-2 py-1.5 outline-none hover:bg-accent focus-visible:bg-accent';

function NotificationRow({
   notification,
   onMarkRead,
}: {
   notification: RecentNotification;
   onMarkRead: () => void;
}) {
   const t = useTranslations('components.NotificationsMenu');
   const { ifixitOrigin } = useAppContext();
   const href = notificationHref(notification.url, ifixitOrigin);
   const sentIcon = notification.icon ?? ({ style: 'solid', name: 'bell' } as const);
   const tile = iconTiles[sentIcon.name];
   const icon = tile?.icon ?? sentIcon;
   const content = (
      <>
         <span
            className={cn(
               'flex size-7 shrink-0 items-center justify-center rounded-lg',
               tile?.className ?? 'bg-gray-100 text-gray-700'
            )}
         >
            <FaIcon name={icon.name} style={icon.style} className="size-4" />
         </span>
         <span className="flex min-w-0 flex-col">
            <span className="break-words">{notification.message}</span>
            <RelativeTime date={notification.date} />
         </span>
      </>
   );

   return (
      <li className="flex items-start rounded-sm hover:bg-accent has-[:focus-visible]:bg-accent">
         {href ? (
            // Opens in a new tab, as the PHP header's bell always did.
            <a
               href={href}
               target="_blank"
               rel="noopener"
               onClick={onMarkRead}
               className="flex min-w-0 grow gap-2 px-2 py-1.5 text-inherit no-underline outline-none"
            >
               {content}
            </a>
         ) : (
            <div className="flex min-w-0 grow gap-2 px-2 py-1.5">{content}</div>
         )}
         <button
            type="button"
            aria-label={t('markRead')}
            className="flex size-8 shrink-0 cursor-pointer items-center justify-center text-gray-400 outline-none hover:text-gray-900 focus-visible:text-gray-900"
            onClick={onMarkRead}
         >
            <FaIcon name="xmark" className="size-3.5" />
         </button>
      </li>
   );
}

// A link into the app means nothing in a browser; send it where the app can be installed.
function notificationHref(url: string, origin: string): string | null {
   if (url === '') {
      return null;
   }
   return new URL(url.startsWith('ifixitmobile://') ? '/go/app' : url, origin).href;
}

function RelativeTime({ date }: { date: string }) {
   const format = useFormatter();
   const now = useNow({ updateInterval: 60_000 });
   return (
      <time dateTime={date} className="text-gray-500">
         {format.relativeTime(new Date(date), now)}
      </time>
   );
}

function LoadingRows() {
   return (
      <div aria-busy="true" className="flex flex-col gap-3 px-2 py-1.5">
         {[0, 1, 2].map(index => (
            <div key={index} className="flex gap-2">
               <Skeleton className="size-4 shrink-0" />
               <div className="flex grow flex-col gap-1.5">
                  <Skeleton className="h-3.5 w-full" />
                  <Skeleton className="h-3.5 w-1/3" />
               </div>
            </div>
         ))}
      </div>
   );
}
